import { useApolloClient } from '@apollo/client';
import Button from '@aurora/shared-client/components/common/Button/Button';
import {
  ButtonVariant,
  LoadingButtonVariant
} from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import useCommunityPolicies from '@aurora/shared-client/components/community/useCommunityPolicies';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import { canAccessPageBuilder as checkCanAccessPageBuilder } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import Icons from '@aurora/shared-client/icons';
import type { NodePageAndParams } from '@aurora/shared-client/routes/adminRoutes';
import { AdminPages } from '@aurora/shared-client/routes/adminRoutes';
import type {
  EditGroupHubPageAndParams,
  GroupHubPageAndParams,
  ManageContentPageAndParams,
  ManageMembersPageAndParams,
  PageAndEmptyParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useAdminUserRoutes from '@aurora/shared-client/routes/useAdminRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { MembershipType } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  GroupHubPoliciesFragment,
  MembershipInformationFragment
} from '@aurora/shared-generated/types/graphql-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserPathParams,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import type { GraphQLFormattedError } from 'graphql';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import endUserComponentRegistry from '../../../features/endUserComponentRegistry';
import { ManageMembersTab, MembershipAction } from '../../../types/enums';
import type {
  GroupHubDetailsQuery,
  JoinNodeMutation,
  JoinNodeMutationVariables,
  LeaveNodeMutation,
  LeaveNodeMutationVariables
} from '../../../types/graphql-types';
import ActionFeedbacks from '../../common/ActionFeedback/ActionFeedbackTypes';
import joinNodeMutation from '../../memberships/JoinMembershipNode.mutation.graphql';
import leaveNodeMutation from '../../memberships/LeaveMembershipNode.mutation.graphql';
import { updateNode } from '../../memberships/MembershipActionHelper';
import { usePageEditorLink } from '../../usePageEditorLink';
import userViewsQuery from '../../users/UserViews.query.graphql';
import useTranslation from '../../useTranslation';
import localStyles from './GroupHubActionMenu.module.pcss';

interface GroupHubActionMenuItem {
  /**
   * Name of the action
   */
  name: MembershipAction;
  /**
   * Method to determine whether to display the action item or not
   * @return whether or not to display the action item.
   */
  policy?: () => boolean;
  /**
   * Event handler for the action taken
   */
  actionHandler: () => void;
}

const ConfirmationDialog = dynamic(
  () => import('@aurora/shared-client/components/common/ConfirmationDialog/ConfirmationDialog')
);

const SendMembershipInviteModal = dynamic(
  () => import('../../memberships/SendMembershipInviteModal/SendMembershipInviteModal')
);

const log = getLog(module);

interface Props {
  /**
   * the grouphub information object
   */
  groupHub: GroupHubDetailsQuery['groupHub'] &
    GroupHubPoliciesFragment &
    MembershipInformationFragment;
}

/**
 * Display Group Hub Action Menu
 *
 * Is not displayed when user does not meet policy criteria to see the any of the menu items
 *
 * @author Badhon Kundu
 */
const GroupHubActionMenu: React.FC<React.PropsWithChildren<Props>> = ({ groupHub }) => {
  const cx = useClassNameMapper(localStyles);
  const client = useApolloClient();
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.GROUP_HUB_ACTION_MENU
  );
  const { addToast } = useToasts();
  const [joinNode] = useMutationWithTracing<JoinNodeMutation>(module, joinNodeMutation);
  const [leaveNode] = useMutationWithTracing<LeaveNodeMutation>(module, leaveNodeMutation);
  const uidSeed = useUIDSeed();
  const { router: endUserRouter } = useEndUserRoutes();
  const { router: adminUserRouter } = useAdminUserRoutes();
  const { groupHubId } = endUserRouter.getPathParams<GroupHubPageAndParams>();
  const { isEditable: isPageEditable } = endUserComponentRegistry.getPageDescriptor(
    EndUserPages.GroupHubPage
  );
  const { link: pageEditorLink } = usePageEditorLink();
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showSendInviteModal, setShowSendInviteModal] = useState<boolean>(false);

  const {
    data: communityPoliciesData,
    loading: communityPoliciesLoading,
    error: communityPoliciesError
  } = useCommunityPolicies(module, {
    useCanAccessPageBuilder: true
  });

  if (textLoading || communityPoliciesLoading) {
    return null;
  }

  if (communityPoliciesError) {
    log.error(`Error fetching policy information: ${communityPoliciesError.message}`);
  }

  const canAccessPageBuilder: boolean = checkCanAccessPageBuilder(communityPoliciesData?.coreNode);

  /**
   * Renders success feedback in a flyout toast when action is successful
   *
   * @param action action taken.
   */
  function renderSuccessFeedback(action: MembershipAction): void {
    const id = uidSeed(`success-${action}-grouphub`);
    const toastProps: ToastProps = {
      toastVariant: ToastVariant.FLYOUT,
      alertVariant: ToastAlertVariant.SUCCESS,
      title: formatMessage(`${action}.successHeader`),
      autohide: true,
      message: formatMessage(`${action}.successMessage`),
      delay: 4000,
      id
    };
    addToast(toastProps);
  }

  /**
   * Renders error feedback in a banner toast when an error occurs while taking an action
   *
   * @param action action taken.
   */
  function renderError(action: MembershipAction): void {
    if (action === MembershipAction.LEAVE_MEMBERSHIP_NODE) {
      setShowConfirmDialog(false);
    }
    const id = uidSeed(`error-${action}-grouphub`);
    const toastProps: ToastProps = {
      toastVariant: ToastVariant.BANNER,
      alertVariant: ToastAlertVariant.DANGER,
      title: formatMessage('errorHeader'),
      autohide: true,
      message: formatMessage('errorMessage'),
      delay: 4000,
      id
    };
    addToast(toastProps);
  }

  /**
   * Function to handle errors resulting from a graphQL mutation call
   *
   * @param rollback rollback function passed from the GraphQL mutate's update method
   * @param errors errors (if any) from GraphQL call
   * @param action action taken.
   */
  function handleError(
    rollback: () => void,
    errors: readonly GraphQLFormattedError[],
    action: MembershipAction
  ): void {
    log.error('Unable to %s Group: %O', action, errors);
    if (rollback) {
      rollback();
    }
    renderError(action);
  }

  /**
   * Event handler for Edit Group Hub Settings action
   */
  async function onEditGroupHubSettingsHandler(): Promise<void> {
    await adminUserRouter.pushRoute<NodePageAndParams>(AdminPages.NodeSettingsPage, {
      nodeId: groupHub.id
    });
  }

  /**
   * Event handler for Edit Group Hub action
   */
  async function onEditGroupHubHandler(): Promise<void> {
    await endUserRouter.pushRoute<EditGroupHubPageAndParams>(EndUserPages.EditGroupHubPage, {
      groupHubId
    });
  }

  /**
   * Event handler for Manage Members action
   */
  async function onManageMembersActionHandler(): Promise<void> {
    await endUserRouter.pushRoute<ManageMembersPageAndParams>(
      EndUserPages.ManageMembersPage,
      {
        [EndUserPathParams.GROUPHUB_ID]: groupHubId
      },
      {
        [EndUserQueryParams.TAB]: ManageMembersTab.MEMBERS
      }
    );
  }

  function renderSendMembershipInviteModal() {
    return (
      <SendMembershipInviteModal
        show={showSendInviteModal}
        onHide={() => setShowSendInviteModal(false)}
        contextNode={groupHub}
      />
    );
  }

  /**
   * Event handler for Join Group Hub action
   */
  async function onJoinMembershipNodeHandler(): Promise<void> {
    const mutationPayload: JoinNodeMutationVariables = {
      nodeId: groupHub.id
    };
    const { errors, rollback } = await updateNode({
      nodeId: groupHub.id,
      mutate: joinNode,
      membershipInformation: groupHub,
      action: MembershipAction.JOIN_MEMBERSHIP_NODE,
      mutationPayload,
      client
    });
    if (errors?.length > 0) {
      handleError(rollback, errors, MembershipAction.JOIN_MEMBERSHIP_NODE);
      return;
    }
    renderSuccessFeedback(MembershipAction.JOIN_MEMBERSHIP_NODE);
  }

  /**
   * Event handler for Request To Join Group Hub action
   */
  async function onRequestToJoinMembershipNodeHandler(): Promise<void> {
    const mutationPayload: JoinNodeMutationVariables = {
      nodeId: groupHub.id
    };
    const { errors, rollback } = await updateNode({
      nodeId: groupHub.id,
      mutate: joinNode,
      membershipInformation: groupHub,
      action: MembershipAction.REQUEST_TO_JOIN,
      mutationPayload
    });
    if (errors?.length > 0) {
      handleError(rollback, errors, MembershipAction.REQUEST_TO_JOIN);
      return;
    }
    renderSuccessFeedback(MembershipAction.REQUEST_TO_JOIN);
  }

  /**
   * Function implements Leave Group Hub action and subsequent flow
   */
  async function leaveGroupHub(): Promise<void> {
    const mutationPayload: LeaveNodeMutationVariables = {
      nodeId: groupHub.id
    };
    const { errors, rollback } = await updateNode({
      nodeId: groupHub.id,
      mutate: leaveNode,
      membershipInformation: groupHub,
      action: MembershipAction.LEAVE_MEMBERSHIP_NODE,
      mutationPayload,
      client
    });
    if (errors?.length > 0) {
      handleError(rollback, errors, MembershipAction.LEAVE_MEMBERSHIP_NODE);
      return;
    }
    if (
      groupHub?.membershipType === MembershipType.Closed ||
      groupHub?.membershipType === MembershipType.ClosedHidden
    ) {
      client.cache.evict({ id: `GroupHub:${groupHub.id}`, broadcast: false });
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'nodes',
        broadcast: false
      });
      if (
        groupHub?.userContext?.canUpdateNode &&
        groupHub?.membershipType === MembershipType.Closed
      ) {
        client.refetchQueries({
          include: [userViewsQuery]
        });
        renderSuccessFeedback(MembershipAction.LEAVE_MEMBERSHIP_NODE);
      }
      if (groupHub?.membershipType === MembershipType.ClosedHidden) {
        await endUserRouter.replaceRoute<PageAndEmptyParams>(EndUserPages.CommunityPage, null, {
          [EndUserQueryParams.ACTION_FEEDBACK]: ActionFeedbacks.LEFT_GROUPHUB,
          [EndUserQueryParams.TIMESTAMP]: Date.now().toString()
        });
      }
    } else {
      renderSuccessFeedback(MembershipAction.LEAVE_MEMBERSHIP_NODE);
    }
  }

  /**
   * Renders confirmation dialog
   */
  const renderConfirmLeaveDialog = (): React.ReactElement => {
    return (
      <ConfirmationDialog
        show={showConfirmDialog}
        onHide={(): void => setShowConfirmDialog(false)}
        onSubmit={leaveGroupHub}
        onCancel={(): void => setShowConfirmDialog(false)}
        titleText={formatMessage('confirmDialogTitle')}
        bodyText={formatMessage(`confirmDialogBody.${groupHub?.membershipType}`)}
        submitButtonVariant={ButtonVariant.DANGER}
        submitButtonType={LoadingButtonVariant.LOADING_BUTTON}
        submitButtonText={formatMessage('confirmDialogSubmit')}
      />
    );
  };

  /**
   * Event handler for Leave Group Hub action
   */
  async function onLeaveMembershipNodeHandler(): Promise<void> {
    if (groupHub?.membershipType === MembershipType.Open) {
      leaveGroupHub();
    } else {
      setShowConfirmDialog(true);
    }
  }

  /**
   * Event handler for Manage Content in GroupHub action
   */
  async function onManageGroupHubContentHandler(): Promise<void> {
    await endUserRouter.pushRoute<ManageContentPageAndParams>(
      EndUserPages.ManageContentPage,
      null,
      {
        [EndUserQueryParams.SEARCH_FILTER_BY_LOCATION]: groupHub.id
      }
    );
  }

  /**
   * Checks for showing the Manage Content button based
   * on user permissions.
   */
  function getPolicyChecksForManageContent(): boolean {
    return (
      checkPolicy(groupHub?.grouphubPolicies.canManageArticleBlog) ||
      checkPolicy(groupHub?.grouphubPolicies.canManageArticleTkb)
    );
  }

  const adminNodeActions: GroupHubActionMenuItem[] = [
    {
      name: MembershipAction.EDIT_GROUP_HUB_SETTINGS,
      policy: (): boolean => checkPolicy(groupHub.grouphubPolicies.canAdminNode),
      actionHandler: onEditGroupHubSettingsHandler
    }
  ];

  const adminActions: GroupHubActionMenuItem[] = [
    {
      name: MembershipAction.MANAGE_GROUP_HUB_CONTENT,
      policy: (): boolean => getPolicyChecksForManageContent(),
      actionHandler: onManageGroupHubContentHandler
    },
    {
      name: MembershipAction.MANAGE_MEMBERS,
      policy: (): boolean => checkPolicy(groupHub.membershipPolicies.canManageMembers),
      actionHandler: onManageMembersActionHandler
    },
    {
      name: MembershipAction.SEND_INVITE,
      policy: (): boolean => checkPolicy(groupHub.membershipPolicies.canSendMembershipInvites),
      actionHandler: () => setShowSendInviteModal(true)
    },
    {
      name: MembershipAction.EDIT_GROUP_HUB,
      policy: (): boolean => groupHub.userContext.canUpdateNode,
      actionHandler: onEditGroupHubHandler
    }
  ];

  const userActions: GroupHubActionMenuItem[] = [
    {
      name: MembershipAction.JOIN_MEMBERSHIP_NODE,
      policy: (): boolean => checkPolicy(groupHub.membershipPolicies.canJoin),
      actionHandler: onJoinMembershipNodeHandler
    },
    {
      name: MembershipAction.LEAVE_MEMBERSHIP_NODE,
      policy: (): boolean => groupHub.isMember,
      actionHandler: onLeaveMembershipNodeHandler
    },
    {
      name: MembershipAction.REQUEST_TO_JOIN,
      policy: (): boolean =>
        checkPolicy(groupHub.membershipPolicies.canRequestToJoin) &&
        !groupHub.userContext.canUpdateNode,
      actionHandler: onRequestToJoinMembershipNodeHandler
    }
  ];

  /**
   * Function to check if the action item is to be displayed
   *
   * @param policy A callback function to determine if action item is to be displayed
   * @return boolean Whether or not to display the action item
   */
  function actionShouldDisplay(policy: () => boolean): boolean {
    if (policy) {
      return policy();
    }
    // If no policy provided, display the action item
    return true;
  }

  const hasAdminNodeItemsToDisplay: boolean =
    adminNodeActions?.filter(action => actionShouldDisplay(action?.policy)).length > 0;
  const hasAdminItemsToDisplay: boolean =
    adminActions?.filter(action => actionShouldDisplay(action?.policy)).length > 0;
  const hasItemsToDisplay: boolean =
    userActions?.filter(action => actionShouldDisplay(action?.policy)).length > 0;

  /**
   * Renders the edit page link in Group Hub Action Menu
   */
  function renderEditPageLink(): React.ReactElement {
    return pageEditorLink(
      <Dropdown.Item
        key={'editPage'}
        className={cx('lia-dropdown-item')}
        data-testid={'GroupHubActionMenuItem.EditPage'}
      >
        {formatMessage('editPage')}
      </Dropdown.Item>
    );
  }

  /**
   * Renders admin node menu items in Group Hub Action Menu
   *
   * @param action The action to be displayed
   */
  function renderAdminNodeMenuItem(action: GroupHubActionMenuItem): React.ReactElement {
    const displayAdminNodeMenuItem = actionShouldDisplay(action?.policy);
    const actionHandler = action?.actionHandler;

    if (displayAdminNodeMenuItem) {
      return (
        <Dropdown.Item
          key={action.name}
          onClick={actionHandler}
          className={cx('lia-dropdown-item')}
          data-testid={`GroupHubActionMenuItem.${action.name}`}
        >
          {formatMessage(`${action.name}.grouphub`)}
        </Dropdown.Item>
      );
    }
    return null;
  }

  /**
   * Renders admin menu items in Group Hub Action Menu
   *
   * @param action The action to be displayed
   */
  function renderAdminMenuItem(action: GroupHubActionMenuItem): React.ReactElement {
    const displayAdminMenuItem = actionShouldDisplay(action?.policy);
    const actionHandler = action?.actionHandler;

    if (displayAdminMenuItem) {
      return (
        <Dropdown.Item
          key={action.name}
          onClick={actionHandler}
          className={cx('lia-dropdown-item')}
          data-testid={`GroupHubActionMenuItem.${action.name}`}
        >
          {formatMessage(`${action.name}.grouphub`)}
        </Dropdown.Item>
      );
    }
    return null;
  }
  /**
   * Renders menu items in Group Hub Action Menu
   *
   * @param action The action to be displayed
   */
  function renderMenuItem(action: GroupHubActionMenuItem): React.ReactElement {
    const displayMenuItem = actionShouldDisplay(action?.policy);
    const actionHandler = action?.actionHandler;

    if (displayMenuItem) {
      return (
        <Dropdown.Item
          key={action.name}
          onClick={actionHandler}
          className={cx('lia-dropdown-item')}
          data-testid={`GroupHubActionMenuItem.${action.name}`}
        >
          {formatMessage(`${action.name}.grouphub`)}
        </Dropdown.Item>
      );
    }
    return null;
  }

  return (
    (hasItemsToDisplay ||
      (isPageEditable && canAccessPageBuilder) ||
      hasAdminItemsToDisplay ||
      hasAdminNodeItemsToDisplay) && (
      <>
        <Dropdown
          className={cx('lia-g-ml-10')}
          title={formatMessage('actionMenu.title')}
          aria-label={formatMessage('actionMenu.ariaLabel')}
        >
          <Dropdown.Toggle
            id="lia-grouphub-action-menu"
            as={Button}
            aria-label={formatMessage('toggleButtonLabel')}
            className={cx('lia-g-icon-btn')}
            variant={ButtonVariant.NO_VARIANT}
          >
            <Icon
              icon={Icons.GearIcon}
              size={IconSize.PX_20}
              color={IconColor.GRAY_900}
              testId="GroupHubActionMenuItem.GearIcon"
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="right"
            aria-labelledby="lia-grouphub-action-menu"
            className={cx('lia-dropdown-menu lia-dropdown-menu-index')}
            popperConfig={dropdownPopperConfig}
            renderOnMount
            data-testid="GroupHubActionMenuItem.dropdown"
          >
            {hasAdminItemsToDisplay && adminActions.map(action => renderAdminMenuItem(action))}
            {hasAdminItemsToDisplay && hasAdminNodeItemsToDisplay && <Dropdown.Divider />}
            {hasAdminNodeItemsToDisplay &&
              adminNodeActions.map(action => renderAdminNodeMenuItem(action))}
            {isPageEditable && canAccessPageBuilder && renderEditPageLink()}
            {(hasAdminNodeItemsToDisplay ||
              (isPageEditable && canAccessPageBuilder) ||
              hasAdminItemsToDisplay) &&
              hasItemsToDisplay && <Dropdown.Divider />}
            {hasItemsToDisplay && userActions.map(action => renderMenuItem(action))}
          </Dropdown.Menu>
        </Dropdown>
        {showConfirmDialog && renderConfirmLeaveDialog()}
        {showSendInviteModal && renderSendMembershipInviteModal()}
      </>
    )
  );
};

export default GroupHubActionMenu;
